import { Link, NavLink, useNavigate } from "react-router-dom";
import { useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useCart } from "@/context/CartContext";
import { useBranding } from "@/context/BrandingContext";
import NotificationBell from "@/components/layout/NotificationBell";
import { Menu, X, LogOut, LayoutGrid, ShieldCheck, ShoppingCart } from "lucide-react";

const primaryLinks = [
  { to: "/store/indicators", label: "Indicators" },
  { to: "/store/expert-advisors", label: "Expert Advisors" },
  { to: "/signals", label: "Signals" },
  { to: "/store/courses", label: "Courses" },
  { to: "/bundles", label: "Bundles" },
  { to: "/brokers", label: "Brokers" },
];
const secondaryLinks = [
  { to: "/insights", label: "Insights" },
  { to: "/about", label: "About" },
  { to: "/contact", label: "Contact" },
];

export default function Navbar() {
  const { user, isAdmin, logout } = useAuth();
  const { count, setOpen: setCartOpen } = useCart();
  const { branding, publicConfig } = useBranding();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const visiblePrimary = primaryLinks.filter((l) => l.to !== "/signals" || publicConfig?.signals_enabled !== false);

  const handleLogout = () => {
    logout();
    navigate("/");
  };

  return (
    <>
    <header
      data-testid="site-navbar"
      className="sticky top-0 z-40 border-b"
      style={{
        background: "rgba(239, 232, 214, 0.9)",
        backdropFilter: "blur(14px) saturate(120%)",
        WebkitBackdropFilter: "blur(14px) saturate(120%)",
        borderColor: "var(--ink-hairline)",
      }}
    >
      <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 lg:px-8">
        <Link to={user ? "/dashboard" : "/"} data-testid="brand-logo" className="flex items-center">
          <img src={branding.logo_url || "/logo.png"} alt={branding.site_name || "HK ForexEdge"} className="h-8 w-auto" onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = "/logo.png"; }} />
        </Link>

        <nav className="hidden items-center gap-1 lg:flex">
          {visiblePrimary.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `rounded-full px-3 py-1.5 text-[14px] font-medium transition-colors ${
                  isActive
                    ? "bg-[color:var(--ink-hairline)] text-[color:var(--ink)]"
                    : "text-[color:var(--ink-body)] hover:text-[color:var(--ink)]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
          <span className="mx-1 h-3 w-px bg-[color:var(--ink-hairline)]" />
          {secondaryLinks.map((l) => (
            <NavLink
              key={l.to}
              to={l.to}
              data-testid={`nav-${l.label.toLowerCase()}`}
              className={({ isActive }) =>
                `rounded-full px-3 py-1.5 text-[14px] font-medium transition-colors ${
                  isActive
                    ? "bg-[color:var(--ink-hairline)] text-[color:var(--ink)]"
                    : "text-[color:var(--ink-body)] hover:text-[color:var(--ink)]"
                }`
              }
            >
              {l.label}
            </NavLink>
          ))}
        </nav>

        <div className="hidden items-center gap-2 lg:flex">
          <button onClick={() => setCartOpen(true)} data-testid="nav-cart" aria-label="Cart"
            className="relative rounded-full p-2 text-[color:var(--ink-muted)] transition-colors hover:bg-[color:var(--ink-hairline)] hover:text-[color:var(--ink)]">
            <ShoppingCart size={16} />
            {count > 0 && <span data-testid="nav-cart-count" className="absolute -right-0.5 -top-0.5 grid h-4 min-w-4 place-items-center rounded-full bg-[color:var(--evergreen,#0a7d43)] px-1 text-[9px] font-bold leading-none text-white">{count}</span>}
          </button>
          {user ? (
            <>
              {isAdmin && (
                <Link to="/admin" data-testid="nav-admin" className="btn-edge-ghost text-sm">
                  <ShieldCheck size={16} /> Admin
                </Link>
              )}
              <NotificationBell />
              <Link to="/dashboard" data-testid="nav-dashboard" className="btn-edge-ghost text-sm">
                <LayoutGrid size={16} /> Dashboard
              </Link>
              <button
                onClick={handleLogout}
                data-testid="nav-logout"
                className="rounded-full p-2 text-[color:var(--ink-muted)] transition-colors hover:bg-[color:var(--ink-hairline)] hover:text-[color:var(--ink)]"
                aria-label="Log out"
              >
                <LogOut size={16} />
              </button>
            </>
          ) : (
            <>
              <Link to="/login" data-testid="nav-login" className="btn-edge-ghost text-sm">
                Sign in
              </Link>
              <Link to="/register" data-testid="nav-register" className="btn-edge text-sm">
                Get access
              </Link>
            </>
          )}
        </div>

        <button
          onClick={() => setOpen((v) => !v)}
          className="rounded-full border border-[color:var(--ink-hairline)] p-2 text-[color:var(--ink)] lg:hidden"
          data-testid="nav-mobile-toggle"
          aria-label="Toggle menu"
        >
          {open ? <X size={18} /> : <Menu size={18} />}
        </button>
      </div>
    </header>

      {/* Mobile drawer */}
      {open && (
        <div className="fixed inset-0 z-[70] lg:hidden" data-testid="nav-mobile-drawer">
          <div
            className="absolute inset-0 bg-[color:var(--ink)]/40 backdrop-blur-sm"
            onClick={() => setOpen(false)}
          />
          <div className="absolute right-0 top-0 flex h-full w-72 max-w-[82%] flex-col border-l border-[color:var(--ink-hairline)] bg-[color:var(--paper)] shadow-2xl">
            <div className="flex items-center justify-between border-b border-[color:var(--ink-hairline)] px-5 py-4">
              <img
                src={branding.logo_url || "/logo.png"}
                alt={branding.site_name || "HK ForexEdge"}
                className="h-7 w-auto"
                onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.src = "/logo.png"; }}
              />
              <button
                onClick={() => setOpen(false)}
                data-testid="nav-mobile-close"
                aria-label="Close menu"
                className="rounded-full p-1.5 text-[color:var(--ink-muted)] transition-colors hover:bg-[color:var(--ink-hairline)] hover:text-[color:var(--ink)]"
              >
                <X size={18} />
              </button>
            </div>

            <nav className="grid flex-1 content-start gap-1 overflow-y-auto px-3 py-4">
              {[...visiblePrimary, ...secondaryLinks].map((l) => (
                <NavLink
                  key={l.to}
                  to={l.to}
                  onClick={() => setOpen(false)}
                  data-testid={`nav-mobile-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className={({ isActive }) =>
                    `rounded-lg px-3 py-2.5 text-sm font-medium transition-colors ${
                      isActive
                        ? "bg-[color:var(--ink-hairline)] text-[color:var(--ink)]"
                        : "text-[color:var(--ink-body)] hover:bg-[color:var(--paper-soft)] hover:text-[color:var(--ink)]"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              ))}
              <button
                onClick={() => { setOpen(false); setCartOpen(true); }}
                data-testid="nav-mobile-cart"
                className="mt-1 flex items-center justify-between rounded-lg px-3 py-2.5 text-sm font-medium text-[color:var(--ink-body)] transition-colors hover:bg-[color:var(--paper-soft)] hover:text-[color:var(--ink)]"
              >
                <span className="inline-flex items-center gap-2"><ShoppingCart size={16} /> Cart</span>
                {count > 0 && (
                  <span className="grid h-5 min-w-5 place-items-center rounded-full bg-[color:var(--evergreen,#0a7d43)] px-1 text-[10px] font-bold leading-none text-white">{count}</span>
                )}
              </button>
            </nav>

            <div className="grid gap-2 border-t border-[color:var(--ink-hairline)] p-4">
              {user ? (
                <>
                  {isAdmin && (
                    <Link to="/admin" onClick={() => setOpen(false)} data-testid="nav-mobile-admin" className="btn-edge-ghost justify-center">
                      <ShieldCheck size={16} /> Admin
                    </Link>
                  )}
                  <Link to="/dashboard" onClick={() => setOpen(false)} data-testid="nav-mobile-dashboard" className="btn-edge-ghost justify-center">
                    <LayoutGrid size={16} /> Dashboard
                  </Link>
                  <button onClick={() => { setOpen(false); handleLogout(); }} data-testid="nav-mobile-logout" className="btn-edge-ghost justify-center">
                    <LogOut size={16} /> Log out
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login" onClick={() => setOpen(false)} data-testid="nav-mobile-login" className="btn-edge-ghost justify-center">
                    Sign in
                  </Link>
                  <Link to="/register" onClick={() => setOpen(false)} data-testid="nav-mobile-register" className="btn-edge justify-center">
                    Get access
                  </Link>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
}
